import React, { useState, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Box,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Link
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  // Infinite tiled pattern from public/pattern.jpg, visible but subdued
  patternLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: "url(/pattern.jpg)",
    backgroundRepeat: "repeat",
    backgroundSize: "auto",
    opacity: 0.18,
    zIndex: 0,
  },
  content: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 420,
    padding: theme.spacing(3),
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4, 3),
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.08)",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    width: "100%",
    color: "#134e4a",
  },
  logo: {
    marginBottom: theme.spacing(2),
    maxWidth: 140,
    width: "100%",
    height: "auto",
    objectFit: "contain",
  },
  title: {
    fontWeight: 700,
    color: "#134e4a",
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: "0.95rem",
    color: "#64748b",
    marginBottom: theme.spacing(3),
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f8fafc",
      "& fieldset": {
        borderColor: "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#0d9488",
        backgroundColor: "#fff",
      },
      "&.Mui-focused fieldset": {
        borderWidth: 2,
        borderColor: "#0d9488",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#0d9488",
    },
  },
  submit: {
    margin: theme.spacing(2.5, 0, 1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    backgroundColor: "#0d9488",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(13, 148, 136, 0.4)",
    "&:hover": {
      backgroundColor: "#0f766e",
      boxShadow: "0 6px 20px rgba(13, 148, 136, 0.45)",
    },
  },
  registerLink: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    width: "100%",
  },
  registerLinkText: {
    color: "#64748b",
    fontSize: "0.9rem",
    "&:hover": {
      color: "#0d9488",
    },
  },
}));

const Login = () => {
  const classes = useStyles();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <div className={classes.patternLayer} aria-hidden="true" />
      <CssBaseline />
      <Box className={classes.content}>
        <Paper elevation={0} className={classes.paper}>
          <img src="/largelogo.png" alt="Logo" className={classes.logo} />
          <Typography component="h1" variant="h5" className={classes.title}>
            {i18n.t("login.title")}
          </Typography>

          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <TextField
              className={classes.textField}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
            />
            <TextField
              className={classes.textField}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                      edge="end"
                      style={{ color: "#64748b" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
            >
              {i18n.t("login.buttons.submit")}
            </Button>
          </form>
        </Paper>
      </Box>
    </div>
  );
};

export default Login;
